import React from "react";
import { PropTypes } from "prop-types";
import { MrbFieldInput, MrbFieldToggle } from "mrb/components";
import { MrbContentLoader } from "mrb/components/loader";
import { MrbFileUploadValidation } from "mrb/components/media";
import {
    NakiNigunAddAlbum,
    NakiNigunArtistEditAlbumList,
    NakiNigunArtistEditPageLayout,
    NakiNigunArtistEditNameLoader,
    NakiNigunAddAlbumLoader,
} from "administration/naki-nigun/components";
import { CoverImageUploadLoader, CoverImageUpload } from "common/components";
import { defaultTemplate } from "common/hoc";
import { mergeCss } from "mrb/common/utils";

function NakiNigunArtistEditTemplate({ nakiNigunArtistEditViewStore, t }) {
    const { form, loaderStore, coverImageUploadViewStore } = nakiNigunArtistEditViewStore;
    const { fileUploadStore } = coverImageUploadViewStore;

    return (
        <NakiNigunArtistEditPageLayout store={nakiNigunArtistEditViewStore} t={t}>
            <div className={mergeCss("row", form.$("isFemale").value && "is-female-content")}>
                <div className="col col-sml-12 col-xxlrg-7 col-huge-8 u-mar--top--sml u-mar--bottom--sml">
                    <div className="c-card--primary">
                        <MrbContentLoader
                            loaderStore={loaderStore}
                            loaderRender={() => <NakiNigunArtistEditNameLoader />}
                        >
                            {() => (
                                <form onSubmit={form.onSubmit}>
                                    <div className="u-animation--fadeIn">
                                        <div className="row">
                                            <div className="col col-sml-12">
                                                <MrbFieldInput
                                                    t={t}
                                                    field={form.$("name")}
                                                    classNameExtend="c-input--outline"
                                                />
                                            </div>
                                            <div className="col col-sml-12 col-huge-6">
                                                <div className="c-toggle__card-list">
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isActive")}
                                                    />
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isPremium")}
                                                    />
                                                    <div className="u-separator--primary u-mar--none"></div>

                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isVisible")}
                                                    />

                                                    <div className="u-separator--primary u-mar--none"></div>

                                                    {/* Every per-device toggle sits together from here down, the
                                                        App included - it used to sit above the Portal setting,
                                                        which read as if it belonged to it. */}
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isVisibleInApp")}
                                                        disabled={form.$("isFemale").value}
                                                    />
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isVisibleInEmbeddedApp")}
                                                        disabled={form.$("isFemale").value}
                                                    />

                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isVisibleInHandheldApp")}
                                                        disabled={form.$("isFemale").value}
                                                    />

                                                    {/* The Kedayo Pink is the only device that carries female
                                                        content, so unlike every toggle above it this one stays
                                                        usable while isFemale is set. */}
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isVisibleInHandheldPinkApp")}
                                                    />

                                                    {/* Female content governs which devices may carry this artist
                                                        at all, so it sits on its own after the per-device toggles
                                                        rather than inside them. Same placement as the preview page
                                                        and the podcast screens. */}
                                                    <div className="u-separator--primary u-mar--none"></div>
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isFemale")}
                                                    />
                                                    <div className="u-separator--primary u-mar--none"></div>

                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("isFeatured")}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </MrbContentLoader>
                    </div>
                </div>

                <div className="col col-sml-12 col-xxlrg-4 u-mar--top--sml u-mar--bottom--sml">
                    <div className="c-card--primary">
                        <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <CoverImageUploadLoader />}>
                            {() => (
                                <CoverImageUpload
                                    classNameExtendUpload="c-upload--tny"
                                    isFullWidthFooterButtons
                                    coverImageUploadViewStore={coverImageUploadViewStore}
                                    showInfo
                                />
                            )}
                        </MrbContentLoader>
                    </div>
                    <MrbFileUploadValidation store={fileUploadStore} />
                </div>
            </div>

            <div className="u-mar--bottom--med">
                <h1 className="u-type--base u-mar--bottom--sml">
                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.EDIT.ADD_ALBUMS.TITLE")}
                </h1>
            </div>

            <div
                className={mergeCss(
                    "c-card--primary u-mar--bottom--xlrg",
                    form.$("isFemale").value && "is-female-content"
                )}
            >
                <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <NakiNigunAddAlbumLoader />}>
                    {() => <NakiNigunAddAlbum nakiNigunArtistEditViewStore={nakiNigunArtistEditViewStore} />}
                </MrbContentLoader>
            </div>

            <div className={mergeCss(form.$("isFemale").value && "is-female-content")}>
                <NakiNigunArtistEditAlbumList nakiNigunArtistEditViewStore={nakiNigunArtistEditViewStore} t={t} />
            </div>
        </NakiNigunArtistEditPageLayout>
    );
}

NakiNigunArtistEditTemplate.propTypes = {
    nakiNigunArtistEditViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

export default defaultTemplate(NakiNigunArtistEditTemplate);
